import type {
  CapitalGlobalAssistanceCatalogItem,
  CapitalGlobalCoverageCatalogItem,
  CapitalGlobalSelectOption,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import { roundCoveragePercent } from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import type { ProposalDetails } from '@/services/quotes/types';
import { getCapitalGlobalInsuranceTypeLabel } from '@/shared/utils/capital-global-insurance-type.util';
import type {
  CalculationCommercialization,
  CalculationCoverageGroup,
  CalculationDataByStipulant,
  CalculationInstallmentRow,
  CalculationSimpleGroup,
  CalculationSummaryCard,
  CalculationViewData,
  StipulantAccordionEntry,
} from '../step-calculation.types';
import {
  buildStipulantAccordionLabel,
  resolveCustomerLegalName,
} from './build-stipulant-accordion-label.util';
import {
  hasRiskMembers,
  resolveRisksForCustomer,
  type EntityRisk,
} from './resolve-risks-for-customer.util';
import { resolveStipulantSegmentCode } from './resolve-stipulant-segment-code.util';
import {
  isCapitalGlobalAssistanceOrCapitalization,
  isCapitalGlobalAssistanceUmbrella,
  isCapitalGlobalAssistanceVariant,
  isCapitalGlobalCapitalizationUmbrella,
  isCapitalGlobalCapitalizationVariant,
} from '@/shared/utils/capital-global-coverage-kind.util';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(value?: number): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatCalculationNumber(value?: number): string {
  // `Number.isFinite` não estreita `number | undefined` para o TypeScript;
  // conferir o undefined explicitamente é o que dá a garantia ao Math.round.
  if (value === undefined || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
}

function resolveGroupProfileLabel(profile?: string): string {
  if (!profile) return '—';
  const map: Record<string, string> = {
    ESCALONADO: 'Escalonado',
    UNIFORME: 'Uniforme',
  };
  return map[profile.toUpperCase()] ?? profile;
}

const PERIOD_LABELS: Record<number, string> = {
  1: 'Anual',
  2: 'Semestral',
  3: 'Quadrimestral',
  4: 'Trimestral',
  6: 'Bimestral',
  12: 'Mensal',
};

/** Ordem de exibição: do menor (Mensal) para o maior (Anual) período. */
const INSTALLMENT_PERIOD_COUNTS = [12, 6, 4, 3, 2, 1] as const;


// ─── Section mappers ────────────────────────────────────────────────────────

function mapInstallments(
  details: ProposalDetails
): CalculationInstallmentRow[] {
  const annualPremium = details.duePremium ?? details.DuePremium;
  if (annualPremium == null) return [];

  return INSTALLMENT_PERIOD_COUNTS.map(count => ({
    periodicity: PERIOD_LABELS[count] ?? `${count}x`,
    premiumValue: formatCurrency(annualPremium / count),
  }));
}

function mapInsuranceDataCard(
  details: ProposalDetails
): CalculationSummaryCard {
  return {
    title: 'Dados do Seguro',
    items: [
      {
        label: 'Tipo de Seguro',
        value: getCapitalGlobalInsuranceTypeLabel(details.typeOfInsurance),
      },
    ],
  };
}

function mapStipulantDataCard(
  details: ProposalDetails,
  segmentLabel?: string
): CalculationSummaryCard {
  const customer = details.customer;
  const segmentCode = resolveStipulantSegmentCode(details);
  const segmentDisplay = segmentLabel?.trim() || segmentCode || '—';

  return {
    title: 'Dados do Estipulante',
    items: [
      {
        label: 'Razão Social',
        value: customer?.socialName ?? customer?.name ?? '—',
      },
      {
        label: 'Segmento',
        value: segmentDisplay,
      },
    ],
  };
}

type CalculationDataCustomer = {
  partnerCount?: number;
  employeeCount?: number;
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
};

/**
 * Um estipulante/sub entra no cálculo quando foi um CNPJ consultado na Diretrix
 * que retornou sócios/colaboradores (ou idades). CAEPF/CPF não trazem esses dados.
 */
function hasCalculationData(customer: CalculationDataCustomer): boolean {
  return (
    (customer.partnerCount ?? 0) > 0 ||
    (customer.employeeCount ?? 0) > 0 ||
    (customer.averagePartnerAge ?? 0) > 0 ||
    (customer.averageEmployeeAge ?? 0) > 0
  );
}

type StipulantCalculationEntry = {
  roleLabel: string;
  name: string;
  employeeCount: number;
  partnerCount: number;
  averageEmployeeAge?: number;
  averagePartnerAge?: number;
};

/**
 * Dados de cálculo por estipulante/sub CNPJ, TODOS vindos da Diretrix (o `customer`):
 * quantidade de sócios/colaboradores (`partnerCount`/`employeeCount`) e idades médias
 * (`average*Age`) — NÃO as vidas cotadas do perfil de risco. CAEPF/CPF (sem esses
 * dados) são omitidos — é o que oculta o bloco quando o grupo é só pessoa física.
 */
function collectStipulantCalculationEntries(
  details: ProposalDetails
): StipulantCalculationEntry[] {
  const customers =
    details.customers && details.customers.length > 0
      ? details.customers
      : details.customer
        ? [details.customer]
        : [];

  const entries: StipulantCalculationEntry[] = [];
  customers.forEach((customer, index) => {
    if (!hasCalculationData(customer)) return;
    const seq = customer.sequenceNumber ?? index;
    entries.push({
      roleLabel: seq === 0 ? 'Estipulante' : `Subestipulante ${seq}`,
      name: resolveCustomerLegalName(customer),
      employeeCount: customer.employeeCount ?? 0,
      partnerCount: customer.partnerCount ?? 0,
      averageEmployeeAge: customer.averageEmployeeAge,
      averagePartnerAge: customer.averagePartnerAge,
    });
  });
  return entries;
}

function mapCalculationDataCard(
  details: ProposalDetails
): CalculationSummaryCard {
  const entries = collectStipulantCalculationEntries(details);

  // Só CAEPF/CPF (nenhum CNPJ com dados): card sem itens → a seção é ocultada.
  if (entries.length === 0) {
    return { title: 'Dados do Cálculo', items: [] };
  }

  const totalEmployees = entries.reduce((sum, e) => sum + e.employeeCount, 0);
  const totalPartners = entries.reduce((sum, e) => sum + e.partnerCount, 0);
  // Idade média do grupo = média PONDERADA pela quantidade de vidas de cada
  // estipulante (média simples de médias distorce com empresas de tamanhos diferentes).
  const employeeAgeWeighted = entries.reduce(
    (sum, e) => sum + (e.averageEmployeeAge ?? 0) * e.employeeCount,
    0
  );
  const partnerAgeWeighted = entries.reduce(
    (sum, e) => sum + (e.averagePartnerAge ?? 0) * e.partnerCount,
    0
  );
  const averageEmployeeAge =
    totalEmployees > 0 ? employeeAgeWeighted / totalEmployees : undefined;
  const averagePartnerAge =
    totalPartners > 0 ? partnerAgeWeighted / totalPartners : undefined;

  return {
    title: 'Dados do Cálculo',
    items: [
      {
        label: 'Quantidade funcionários',
        value: formatCalculationNumber(totalEmployees),
      },
      {
        label: 'Quantidade sócios',
        value: formatCalculationNumber(totalPartners),
      },
      {
        label: 'Idade média funcionários',
        value: formatCalculationNumber(averageEmployeeAge),
      },
      {
        label: 'Idade média sócios',
        value: formatCalculationNumber(averagePartnerAge),
      },
    ],
  };
}

/**
 * Detalhamento por estipulante/sub. Só quando há 2+ CNPJs com dados — com um só,
 * o card de "Dados do Cálculo" já mostra tudo (seria redundante).
 */
function mapCalculationDataByStipulant(
  details: ProposalDetails
): CalculationDataByStipulant[] {
  const entries = collectStipulantCalculationEntries(details);
  if (entries.length < 2) return [];

  return entries.map(entry => ({
    roleLabel: entry.roleLabel,
    name: entry.name,
    employees: {
      count: formatCalculationNumber(entry.employeeCount),
      averageAge: formatCalculationNumber(entry.averageEmployeeAge),
    },
    partners: {
      count: formatCalculationNumber(entry.partnerCount),
      averageAge: formatCalculationNumber(entry.averagePartnerAge),
    },
  }));
}

function mapRiskDataCardForEntity(
  details: ProposalDetails,
  sequenceNumber: number
): CalculationSummaryCard {
  const profiles = (details.riskProfileList ?? []).filter(
    p => p.sequenceNumber === sequenceNumber
  );

  if (!profiles.length) {
    return { title: 'Dados do Risco', items: [] };
  }

  const groupProfile = resolveGroupProfileLabel(profiles[0]?.groupProfile);

  const totalPartners = profiles.reduce(
    (sum, e) => sum + (e.partnersAndDirectors?.livesQuantity ?? 0),
    0
  );
  const partnersCapital = profiles.reduce(
    (sum, e) =>
      sum +
      (e.partnersAndDirectors?.livesQuantity ?? 0) *
        (e.partnersAndDirectors?.individualCapital ?? 0),
    0
  );

  const totalEmployees = profiles.reduce(
    (sum, e) => sum + (e.employees?.livesQuantity ?? 0),
    0
  );
  const employeesCapital = profiles.reduce(
    (sum, e) =>
      sum +
      (e.employees?.livesQuantity ?? 0) * (e.employees?.individualCapital ?? 0),
    0
  );

  const isUniformCombinedProfile = profiles.every(profile => {
    const partners = profile.partnersAndDirectors;
    const employees = profile.employees;

    return (
      profile.groupProfile?.toUpperCase() === 'UNIFORME' &&
      partners?.livesQuantity > 0 &&
      partners.livesQuantity === employees?.livesQuantity &&
      partners.individualCapital === employees?.individualCapital
    );
  });
  const combinedCapital = partnersCapital;
  const totalCapital = partnersCapital + employeesCapital;
  const items = [{ label: 'Perfil do Grupo', value: groupProfile }];

  if (isUniformCombinedProfile) {
    items.push({
      label: `${totalPartners} Colaborador(es) e Sócio(s)`,
      value: formatCurrency(combinedCapital),
    });
  } else if (totalPartners > 0) {
    items.push({
      label: `${totalPartners} Sócio(s)`,
      value: formatCurrency(partnersCapital),
    });
  }

  if (!isUniformCombinedProfile && totalEmployees > 0) {
    items.push({
      label: `${totalEmployees} Colaborador(es)`,
      value: formatCurrency(employeesCapital),
    });
  }

  const displayedTotalCapital = isUniformCombinedProfile
    ? combinedCapital
    : totalCapital;
  if (displayedTotalCapital > 0) {
    items.push({
      label: 'Capital Total',
      value: formatCurrency(displayedTotalCapital),
    });
  }

  return { title: 'Dados do Risco', items };
}

type StipulantAccordionOptions = {
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceFuneralOptions: CapitalGlobalSelectOption[];
  capitalizationOptions: CapitalGlobalSelectOption[];
};

function mapStipulantAccordions(
  details: ProposalDetails,
  options: StipulantAccordionOptions
): StipulantAccordionEntry[] {
  const customers =
    details.customers && details.customers.length > 0
      ? [...details.customers].sort(
          (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0)
        )
      : [{ ...details.customer, sequenceNumber: 0 }];

  const risks = (details.lobs?.[0]?.risks ?? []) as EntityRisk[];

  return customers.map((customer, index) => {
    const seq = customer.sequenceNumber ?? index;
    const label = buildStipulantAccordionLabel(seq, customer);
    const resolved = resolveRisksForCustomer(seq, risks);
    const assistanceOptions = {
      assistanceCatalog: options.assistanceCatalog,
      assistanceFuneralOptions: options.assistanceFuneralOptions,
      capitalizationOptions: options.capitalizationOptions,
      // As variantes numeradas (`093_GLB_ASS_02`…) são COBERTURAS no catálogo,
      // não benefícios — o nome delas está aqui, não no `assistanceCatalog`.
      coverageCatalog: options.coverageCatalog,
    };

    return {
      label,
      riskDataCard: mapRiskDataCardForEntity(details, seq),
      coverageGroups: mapCoverageGroupsForEntity(
        resolved,
        seq,
        details,
        options.coverageCatalog
      ),
      assistancesCapitalizationGroups: mapAssistancesForEntity(
        resolved,
        assistanceOptions
      ),
    };
  });
}

type EntityCoverage = NonNullable<
  NonNullable<NonNullable<ProposalDetails['lobs']>[0]['risks']>[0]['coverages']
>[0];

function readIndividualCapitalFromRisk(risk: EntityRisk | undefined): number {
  if (risk?.individualCapital != null && risk.individualCapital > 0) {
    return risk.individualCapital;
  }
  return 0;
}

function resolveIndividualCapitalForEntity(
  sequenceNumber: number,
  details: ProposalDetails
): number {
  const profile = (details.riskProfileList ?? []).find(
    p => p.sequenceNumber === sequenceNumber
  );
  const p = profile?.partnersAndDirectors?.individualCapital ?? 0;
  const e = profile?.employees?.individualCapital ?? 0;
  return Math.max(p, e);
}

function mapCoverageGroupsForEntity(
  resolved: ReturnType<typeof resolveRisksForCustomer>,
  sequenceNumber: number,
  details: ProposalDetails,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[]
): CalculationCoverageGroup[] {
  if (resolved.profile === 'ESCALONADO') {
    const groups: CalculationCoverageGroup[] = [];

    if (hasRiskMembers(resolved.partners)) {
      groups.push(
        ...mapCoverageGroupsFromList(
          resolved.partners?.coverages ?? [],
          coverageCatalog,
          readIndividualCapitalFromRisk(resolved.partners) ||
            details.riskProfileList?.find(
              p => p.sequenceNumber === sequenceNumber
            )?.partnersAndDirectors?.individualCapital ||
            0,
          'Sócios'
        )
      );
    }

    if (hasRiskMembers(resolved.employees)) {
      groups.push(
        ...mapCoverageGroupsFromList(
          resolved.employees?.coverages ?? [],
          coverageCatalog,
          readIndividualCapitalFromRisk(resolved.employees) ||
            details.riskProfileList?.find(
              p => p.sequenceNumber === sequenceNumber
            )?.employees?.individualCapital ||
            0,
          'Colaboradores'
        )
      );
    }

    return groups;
  }

  const uniform = resolved.uniform;
  const individualCapital =
    readIndividualCapitalFromRisk(uniform) ||
    resolveIndividualCapitalForEntity(sequenceNumber, details);

  return mapCoverageGroupsFromList(
    uniform?.coverages ?? [],
    coverageCatalog,
    individualCapital
  );
}

function mapAssistancesForEntity(
  resolved: ReturnType<typeof resolveRisksForCustomer>,
  options: AssistanceOptions
): CalculationSimpleGroup[] {
  const sourceRisk =
    resolved.profile === 'ESCALONADO'
      ? (resolved.partners ?? resolved.employees)
      : resolved.uniform;

  return mapAssistancesCapitalizationGroupsFromList(
    sourceRisk?.coverages ?? [],
    options
  );
}

/**
 * Mesma precisão do step de coberturas (2 casas): arredondar para inteiro
 * mentia o valor — R$ 5.000 sobre 84.000 é 5,95%, não "6%". Vírgula decimal
 * para acompanhar o restante do resumo (moeda e comercialização em pt-BR).
 */
function formatPercent(
  value: number | undefined,
  denominator: number
): string | undefined {
  if (!value || !denominator) return undefined;
  const pct = (value / denominator) * 100;
  return `${roundCoveragePercent(pct).replace('.', ',')}%`;
}

function isCoverageBasic(
  code: string,
  catalogMap: Map<string, CapitalGlobalCoverageCatalogItem>
): boolean {
  const item = catalogMap.get(code);
  if (item?.required === true) return true;
  // Fallback: "Morte" (exata, case-insensitive) é sempre básica por regra de negócio
  return /^morte$/i.test((item?.title ?? '').trim());
}

function mapCoverageGroupsFromList(
  entityCoverages: EntityCoverage[],
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  individualCapital: number,
  groupLabel?: string
): CalculationCoverageGroup[] {
  // Não filtra por `isFinalLevelCt === 'Y'`: cobertura com níveis de capital
  // (`9300026` Auxílio Funeral, `8200020`) vem como `'N'` porque tem benefício
  // aninhado, e na tela de seleção ela é uma LINHA DA TABELA como qualquer outra
  // — o benefício ali é só o valor escolhido. Com o filtro, sumia das telas.
  const coverages = entityCoverages.filter(
    c => !isCapitalGlobalAssistanceOrCapitalization(c.productElementCode)
  );

  if (!coverages.length) return [];

  const catalogMap = new Map(coverageCatalog.map(c => [c.code, c]));

  const basicCoverages = coverages.filter(c =>
    isCoverageBasic(c.productElementCode ?? '', catalogMap)
  );
  const optionalCoverages = coverages.filter(
    c => !isCoverageBasic(c.productElementCode ?? '', catalogMap)
  );

  // Capital da Morte é referência para calcular % das coberturas opcionais
  const morteCoverage =
    basicCoverages.find(c =>
      /^morte$/i.test(
        (catalogMap.get(c.productElementCode ?? '')?.title ?? '').trim()
      )
    ) ?? basicCoverages[0];
  const morteSumInsured = morteCoverage?.sumInsured ?? 0;

  const basicItems = basicCoverages.map(c => ({
    name:
      catalogMap.get(c.productElementCode ?? '')?.title ??
      c.productElementCode ??
      '—',
    capital: formatCurrency(c.sumInsured),
    percent: formatPercent(c.sumInsured, individualCapital),
  }));

  const optionalItems = optionalCoverages.map(c => ({
    name:
      catalogMap.get(c.productElementCode ?? '')?.title ??
      c.productElementCode ??
      '—',
    capital: formatCurrency(c.sumInsured),
    percent: formatPercent(c.sumInsured, morteSumInsured),
  }));

  const prefix = groupLabel ? `${groupLabel} — ` : '';
  const groups: CalculationCoverageGroup[] = [];
  if (basicItems.length) {
    groups.push({ title: `${prefix}Básicas`, items: basicItems });
  }
  if (optionalItems.length) {
    groups.push({ title: `${prefix}Adicionais`, items: optionalItems });
  }
  return groups;
}

type AssistanceOptions = {
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceFuneralOptions: CapitalGlobalSelectOption[];
  capitalizationOptions: CapitalGlobalSelectOption[];
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
};

/**
 * Nome da PRÓPRIA cobertura (variantes numeradas, ex.: `093_GLB_ASS_02` =
 * "Assistência Funeral Individual").
 *
 * A fonte é o catálogo de COBERTURAS: no catálogo do produto a variante é uma
 * cobertura (`category: 'Assistências'`, `sumInsuredDefinition:
 * 'BENEFIT_SELECTION'`), não um benefício — procurá-la no `assistanceCatalog`,
 * que só tem os toggles, devolvia o código cru na tela.
 */
function resolveCoverageOwnName(
  coverage: EntityCoverage,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[]
): string {
  const code = coverage.productElementCode ?? '';
  return coverageCatalog.find(c => c.code === code)?.title ?? code;
}

function resolveBenefitName(
  code: string,
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[],
  assistanceFuneralOptions: CapitalGlobalSelectOption[]
): string {
  const fromCatalog = assistanceCatalog.find(a => a.code === code);
  if (fromCatalog) return fromCatalog.title;
  const fromOptions = assistanceFuneralOptions.find(o => o.value === code);
  if (fromOptions) return fromOptions.label;
  return code;
}

function mapAssistancesCapitalizationGroupsFromList(
  entityCoverages: EntityCoverage[],
  {
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    coverageCatalog,
  }: AssistanceOptions
): CalculationSimpleGroup[] {
  const groups: CalculationSimpleGroup[] = [];

  // Contêiner base: os BENEFÍCIOS são os itens (toggles na seleção).
  const assistanceBenefits = entityCoverages
    .filter(c => isCapitalGlobalAssistanceUmbrella(c.productElementCode))
    .flatMap(c => c.benefits ?? [])
    .map(b =>
      resolveBenefitName(
        b.productElementCode ?? '',
        assistanceCatalog,
        assistanceFuneralOptions
      )
    )
    .filter(Boolean);

  // Variante numerada: o item é a PRÓPRIA cobertura ("Assistência Funeral
  // Familiar") — o benefício aninhado é o nível de capital, não o nome.
  const assistanceVariants = entityCoverages
    .filter(c => isCapitalGlobalAssistanceVariant(c.productElementCode))
    .map(c => resolveCoverageOwnName(c, coverageCatalog))
    .filter(Boolean);

  assistanceBenefits.push(...assistanceVariants);

  if (assistanceBenefits.length) {
    groups.push({ title: 'Assistências', items: assistanceBenefits });
  }

  const capitalizationBenefits = entityCoverages
    .filter(c => isCapitalGlobalCapitalizationUmbrella(c.productElementCode))
    .flatMap(c => c.benefits ?? [])
    .map(b => {
      const fromOptions = capitalizationOptions.find(
        o => o.value === b.productElementCode
      );
      return fromOptions?.label ?? b.productElementCode ?? '';
    })
    .filter(Boolean);

  capitalizationBenefits.push(
    ...entityCoverages
      .filter(c => isCapitalGlobalCapitalizationVariant(c.productElementCode))
      .map(c => resolveCoverageOwnName(c, coverageCatalog))
      .filter(Boolean)
  );

  if (capitalizationBenefits.length) {
    groups.push({ title: 'Capitalização', items: capitalizationBenefits });
  }

  return groups;
}

/**
 * Comercialização do resumo: os percentuais digitados no step (o catálogo de
 * combos foi removido). O backend devolve em `commercialization`, já em percent
 * mascarado pt-BR ("20,00") — aqui só acrescentamos o sufixo de exibição.
 */
function mapCommercialization(
  details: ProposalDetails
): CalculationCommercialization {
  const saved = details.commercialization;
  // Percentual não preenchido vale zero (mesma regra do resumo do PME); o back
  // devolve string VAZIA, então `??` não bastaria.
  const pct = (value?: string) => `${value?.trim() || '0,00'} %`;

  return {
    brokeragePercent: pct(saved?.brokeragePercent),
    agencingPercent: pct(saved?.agencingPercent),
  };
}

// ─── Catálogos ───────────────────────────────────────────────────────────────

export type CalculationCatalogs = {
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceFuneralOptions: CapitalGlobalSelectOption[];
  capitalizationOptions: CapitalGlobalSelectOption[];
  segmentLabel?: string;
};

// ─── Main export ─────────────────────────────────────────────────────────────

export function mapProposalDetailsToCalculation(
  details: ProposalDetails,
  catalogs: CalculationCatalogs
): CalculationViewData {
  return {
    installments: mapInstallments(details),
    insuranceDataCard: mapInsuranceDataCard(details),
    stipulantDataCard: mapStipulantDataCard(details, catalogs.segmentLabel),
    calculationDataCard: mapCalculationDataCard(details),
    calculationDataByStipulant: mapCalculationDataByStipulant(details),
    stipulantAccordions: mapStipulantAccordions(details, {
      coverageCatalog: catalogs.coverageCatalog,
      assistanceCatalog: catalogs.assistanceCatalog,
      assistanceFuneralOptions: catalogs.assistanceFuneralOptions,
      capitalizationOptions: catalogs.capitalizationOptions,
    }),
    commercialization: mapCommercialization(details),
  };
}
